import { FC, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WrapInLayout } from '../layouts';
import { getErrorFromCatch } from '../api/api';
import { orderApi } from '../api/order.api';
import { msg } from '../msg';
import Loading from '../components/common/loading/Loading';
import OrderMain from '../components/orders/details/OrderMain';
import OrderBilling from '../components/orders/details/OrderBilling';
import OrderShipping from '../components/orders/details/OrderShipping';
import OrderItems from '../components/orders/details/OrderItems';
import OrderReplacements from '../components/orders/details/OrderReplacements';
import OrderStatusLog from '../components/orders/details/OrderStatusLog';

// --
// -- Same blocks, in the same order, as the old order_update page: main info
// -- (with the refund tables), billing, shipping, items, replacements, and the
// -- status history that page reached through "Changes History".
// --
// -- Not carried over: everything SMS. The old page previewed an SMS beside
// -- the comments, had a "Send SMS" checkbox and reported whether one went
// -- out; none of that is here.
// --
const OrderDetailsPage: FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { data: order, isFetching, isError, error } = orderApi.useGetOrderQuery(orderId || '', {
    skip: !orderId,
  });

  useEffect(() => {
    isError && error && toast.error(`${msg('order-load-failed')} ${getErrorFromCatch(error)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  return (
    <WrapInLayout title={`Order ${orderId || ''}`} layout="dashboard">
      <div className="flex items-center gap-4 mb-4">
        <Link to="/orders" className="text-primary hover:text-primary_hover underline underline-offset-2">
          &larr; Back to Orders
        </Link>
        <h1 className="text-xl font-semibold">Order #{orderId}</h1>
        {order?.status ? <span className="text-text_pale">{order.status}</span> : null}
      </div>

      {isFetching && !order
        ? <Loading />
        : !order
          ? <div className="bg-white rounded-lg shadow-sm p-6 text-text_pale">{msg('empty-order-id')}</div>
          : (
            <div className="flex flex-col gap-4 pb-6">
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 items-start">
                <OrderMain order={order} />
                <OrderBilling order={order} />
              </div>

              <OrderShipping order={order} />
              <OrderItems order={order} />
              <OrderReplacements order={order} />
              <OrderStatusLog order={order} />
            </div>
          )
      }
    </WrapInLayout>
  );
};

export default OrderDetailsPage;
